import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Download, Loader2 } from "lucide-react";
import { downloadImage } from "./GalleryCard";

const Lightbox = ({ items, index, onClose, onIndex, lang, tx }) => {
  const [busy, setBusy] = useState(false);
  const item = items[index];
  const title = item ? item[lang] : "";

  const prev = useCallback(() => onIndex((index - 1 + items.length) % items.length), [index, items.length, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % items.length), [index, items.length, onIndex]);

  useEffect(() => {
    if (!item) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [item, onClose, prev, next]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-jannah-green-dark/95 backdrop-blur-md flex items-center justify-center"
          onClick={onClose}
          data-testid="gallery-lightbox"
        >
          <div className="absolute inset-0 pattern-overlay opacity-10" />
          <button onClick={onClose} className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-jannah-gold transition-colors" aria-label={tx.close} data-testid="lightbox-close">
            <X size={20} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-jannah-gold transition-colors" aria-label="Previous" data-testid="lightbox-prev">
            <ChevronLeft size={24} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-jannah-gold transition-colors" aria-label="Next" data-testid="lightbox-next">
            <ChevronRight size={24} />
          </button>

          <div className="relative max-w-6xl w-full px-16 md:px-24" onClick={(e) => e.stopPropagation()}>
            <AnimatePresence mode="wait">
              <motion.img
                key={item.id}
                src={item.src}
                alt={title}
                initial={{ opacity: 0, scale: 0.92, x: 40 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.96, x: -40 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="mx-auto max-h-[72vh] w-auto rounded-2xl shadow-2xl border border-white/10 object-contain"
                data-testid="lightbox-image"
              />
            </AnimatePresence>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-white">
              <div>
                <div className="text-xl font-bold" data-testid="lightbox-title">{title}</div>
                <div className="text-sm text-white/60">{index + 1} / {items.length} · <span className="uppercase tracking-wide">{item.cat}</span> · {tx.hint}</div>
              </div>
              <button
                onClick={() => downloadImage(item.src, title, setBusy)}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full btn-gold font-bold uppercase tracking-wider text-xs transition-colors"
                data-testid="lightbox-download"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {busy ? tx.downloading : tx.download}
              </button>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Lightbox;
